/**
 * Presentational pieces of `/tutorial`. Everything lives inside `.mk` (light-only marketing tokens),
 * so no shadcn primitive and no `<Markdown>` is used here: both follow the app theme and would break
 * the page for a visitor whose stored theme is dark.
 */
import { useEffect, useRef, useState } from 'react'
import { Check, Copy, Info, ShieldAlert, Sparkles } from 'lucide-react'
import { cn } from '#/lib/utils'
import { m } from '#/paraglide/messages'
import type { TutBlock, TutSection, TutStep } from './tutorial-types'

/** A command block with a copy button. Commands are never translated, so the text is shared by both locales. */
export function TutCode({ lines, caption }: { lines: readonly string[]; caption?: string }) {
  const [copied, setCopied] = useState(false)
  const timer = useRef<number | undefined>(undefined)
  const text = lines.join('\n')

  useEffect(() => {
    return () => {
      window.clearTimeout(timer.current)
    }
  }, [])

  const copy = () => {
    void navigator.clipboard
      ?.writeText(text)
      .then(() => {
        setCopied(true)
        window.clearTimeout(timer.current)
        timer.current = window.setTimeout(() => setCopied(false), 1800)
      })
      .catch(() => undefined)
  }

  return (
    <div className="mk-tut-code">
      <div className="mk-tut-code-bar">
        <span>{caption ?? m.tut_terminal()}</span>
        <button type="button" onClick={copy} aria-label={m.tut_copy()}>
          {copied ? <Check className="size-3.5" aria-hidden /> : <Copy className="size-3.5" aria-hidden />}
          {copied ? m.tut_copied() : m.tut_copy()}
        </button>
      </div>
      <pre>
        <code>{text}</code>
      </pre>
    </div>
  )
}

const NOTE_ICON = { info: Info, warn: ShieldAlert, ok: Sparkles }

function TutNote({ tone, title, text }: { tone: 'info' | 'warn' | 'ok'; title: string; text: string }) {
  const Icon = NOTE_ICON[tone]
  return (
    <div className={cn('mk-tut-note', `mk-tut-note--${tone}`)}>
      <Icon className="mk-tut-note-icon size-4" aria-hidden />
      <div>
        <strong>{title}</strong>
        <p>{text}</p>
      </div>
    </div>
  )
}

function TutTable({ head, rows }: { head: readonly string[]; rows: readonly (readonly string[])[] }) {
  return (
    <div className="mk-tut-table">
      <table>
        <thead>
          <tr>
            {head.map((h, i) => (
              <th key={i} scope="col">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function TutBlocks({ blocks }: { blocks: readonly TutBlock[] }) {
  return (
    <>
      {blocks.map((block, i) => {
        if (block.kind === 'p') return <p key={i} className="mk-tut-p">{block.text}</p>
        if (block.kind === 'list')
          return (
            <ul key={i} className="mk-tut-list">
              {block.items.map((item, j) => (
                <li key={j}>{item}</li>
              ))}
            </ul>
          )
        if (block.kind === 'code') return <TutCode key={i} lines={block.lines} caption={block.caption} />
        if (block.kind === 'note') return <TutNote key={i} tone={block.tone} title={block.title} text={block.text} />
        return <TutTable key={i} head={block.head} rows={block.rows} />
      })}
    </>
  )
}

function TutSteps({ steps }: { steps: readonly TutStep[] }) {
  return (
    <ol className="mk-tut-steps">
      {steps.map((step, i) => (
        <li key={i}>
          <span className="mk-tut-step-num" aria-hidden>
            {i + 1}
          </span>
          <div className="mk-tut-step-body">
            <h3>{step.title}</h3>
            <TutBlocks blocks={step.blocks} />
          </div>
        </li>
      ))}
    </ol>
  )
}

/** One tutorial chapter: anchor id, heading, optional intro blocks, numbered steps, outro and honest limits. */
export function TutChapter({ id, index, section }: { id: string; index: number; section: TutSection }) {
  return (
    <section id={id} className="mk-tut-section">
      <div className="mk-tut-head">
        <p className="mk-overline">
          <span className="mk-tut-index">{String(index).padStart(2, '0')}</span> {section.overline}
        </p>
        <h2>{section.title}</h2>
        <p className="mk-lead">{section.lead}</p>
      </div>
      {section.intro ? <TutBlocks blocks={section.intro} /> : null}
      {section.steps ? <TutSteps steps={section.steps} /> : null}
      {section.outro ? <TutBlocks blocks={section.outro} /> : null}
      {section.limits ? (
        <div className="mk-tut-limits">
          <strong>{section.limits.title}</strong>
          <ul>
            {section.limits.items.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  )
}

/**
 * Sticky table of contents. The page scrolls inside a full-height container that fills the viewport,
 * so viewport-rooted IntersectionObserver tracking is correct here.
 */
export function TutToc({ title, items }: { title: string; items: readonly { id: string; label: string }[] }) {
  const [active, setActive] = useState<string | null>(null)

  useEffect(() => {
    const els = items.map((i) => document.getElementById(i.id)).filter((e): e is HTMLElement => Boolean(e))
    if (!els.length) return
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible[0]) setActive(visible[0].target.id)
      },
      { rootMargin: '-88px 0px -70% 0px', threshold: 0 },
    )
    els.forEach((el) => io.observe(el))
    return () => {
      io.disconnect()
    }
  }, [items])

  return (
    <nav className="mk-tut-toc" aria-label={title}>
      <p className="mk-tut-toc-title">{title}</p>
      <ol>
        {items.map((item, i) => (
          <li key={item.id}>
            <a href={`#${item.id}`} className={active === item.id ? 'is-active' : undefined}>
              <span aria-hidden>{String(i + 1).padStart(2, '0')}</span>
              {item.label}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  )
}

/** The same list as a native disclosure, for phones where the sidebar is hidden. */
export function TutTocMobile({ title, items }: { title: string; items: readonly { id: string; label: string }[] }) {
  return (
    <details className="mk-tut-toc-mobile">
      <summary>{title}</summary>
      <ol>
        {items.map((item, i) => (
          <li key={item.id}>
            <a href={`#${item.id}`}>
              <span aria-hidden>{String(i + 1).padStart(2, '0')}</span>
              {item.label}
            </a>
          </li>
        ))}
      </ol>
    </details>
  )
}
